/**
 * Categorias e enums do endpoint **Cleaning Report - Details** (Pudu Cloud API).
 *
 * Documentação oficial (validar enums com a tabela “Description” / 说明 da página):
 * - EN: {@link https://open.pudutech.com/en/cloud-api/hu2812hufoosp0te289jed5q}
 * - ZH: {@link https://open.pudutech.com/zh/cloud-api/hu2812hufoosp0te289jed5q}
 *
 * Resumo estrutural (campos categóricos típicos no JSON de detalhe):
 * | Campo | Notas |
 * |-------|--------|
 * | `status` | Estado da tarefa (0–6). Usar `getCleaningTaskStatusLabel` em `cleaningReportUtils.js`. |
 * | `mode` | Modo de limpeza principal (ex.: 1 molhada, 2 seca). `getCleaningModeLabel`. |
 * | `sub_mode` | Submodo; `0` costuma ser padrão — demais valores conforme doc do modelo. |
 * | `config` | Objeto JSON com níveis (muitas chaves *speed* / *suction* / escovas em 0–2). |
 * | `floor_list[].result.status` | Mesma família de estados que a tarefa. |
 * | `floor_list[].area_array[].type` | Tipo de zona no mapa (ex.: 0 área normal, 3 rota ensinada — confirmar na doc). |
 *
 * @module cleaningReportPuduCategories
 */

export const PUDU_CLEANING_REPORT_DETAILS_DOC_URL_EN =
    'https://open.pudutech.com/en/cloud-api/hu2812hufoosp0te289jed5q';

export const PUDU_CLEANING_REPORT_DETAILS_DOC_URL_ZH =
    'https://open.pudutech.com/zh/cloud-api/hu2812hufoosp0te289jed5q';

const CONFIG_PARAM_LABELS = {
    pt_BR: {
        mode: 'Modo (parâmetros)',
        type: 'Tipo de tarefa',
        vacuum_speed: 'Velocidade do vácuo',
        vacuum_suction: 'Sucção do vácuo',
        wash_speed: 'Velocidade de lavagem',
        wash_suction: 'Sucção de lavagem',
        wash_wash: 'Intensidade de lavagem',
        left_brush: 'Escova esquerda',
        right_brush: 'Escova direita',
        right_vacuum_suction: 'Sucção vácuo (direita)',
    },
    en_US: {
        mode: 'Mode (parameters)',
        type: 'Task type',
        vacuum_speed: 'Vacuum speed',
        vacuum_suction: 'Vacuum suction',
        wash_speed: 'Wash speed',
        wash_suction: 'Wash suction',
        wash_wash: 'Wash intensity',
        left_brush: 'Left brush',
        right_brush: 'Right brush',
        right_vacuum_suction: 'Right vacuum suction',
    },
};

const LEVEL_012 = {
    pt_BR: { 0: 'Mínimo', 1: 'Médio', 2: 'Máximo' },
    en_US: { 0: 'Minimum', 1: 'Medium', 2: 'Maximum' },
};

/**
 * Rótulo amigável para chave do objeto `config` (detalhe da limpeza).
 */
export function getCleaningConfigParamLabel(key, localeCode = 'pt_BR') {
    const map = CONFIG_PARAM_LABELS[localeCode] || CONFIG_PARAM_LABELS.pt_BR;
    return map[key] != null ? map[key] : key;
}

/**
 * Níveis 0–2 usados em vários campos speed/suction/brush na API de limpeza.
 * Confirmar significado exato na documentação Pudu do seu modelo.
 */
export function getCleaningConfigLevelLabel012(value, localeCode = 'pt_BR') {
    const n = Number(value);
    const L = LEVEL_012[localeCode] || LEVEL_012.pt_BR;
    if (Object.prototype.hasOwnProperty.call(L, n)) return L[n];
    return value != null ? String(value) : '—';
}

/**
 * Exibe valor bruto do config; para chaves típicas com escala 0–2, acrescenta rótulo.
 */
export function formatCleaningConfigValueDisplay(key, rawValue, localeCode) {
    if (rawValue === null || rawValue === undefined || rawValue === '') {
        return '—';
    }
    const k = String(key);
    const n = Number(rawValue);
    const looksLikeLevel =
        /speed|suction|wash|brush/i.test(k) &&
        Number.isFinite(n) &&
        n >= 0 &&
        n <= 2;
    if (looksLikeLevel) {
        return `${rawValue} (${getCleaningConfigLevelLabel012(
            rawValue,
            localeCode
        )})`;
    }
    return String(rawValue);
}

/**
 * `sub_mode` no relatório de detalhe. Valores ≠ 0 dependem do firmware/modelo.
 */
export function getCleaningSubModeLabel(subMode, localeCode = 'pt_BR') {
    const s = Number(subMode);
    const en = localeCode === 'en_US';
    if (subMode === null || subMode === undefined || subMode === '') {
        return '—';
    }
    if (!Number.isFinite(s)) return String(subMode);
    if (s === 0) return en ? 'Default' : 'Padrão';
    return en ? `Sub mode ${s}` : `Submodo ${s}`;
}

/**
 * `area_array[].type` em `floor_list`. Valores além de 0 e 3: conferir doc.
 */
export function getCleaningAreaTypeLabel(type, intl = null) {
    const n = Number(type);

    // Suporte legado se `intl` não for injetado (fallback sem quebrar apps)
    if (!intl || typeof intl.formatMessage !== 'function') {
        const localeCode = typeof intl === 'string' ? intl : 'pt_BR';
        const en = localeCode === 'en_US';
        const legacyMap = {
            0: en ? 'Cleaning zone' : 'Zona de limpeza',
            1: en ? 'Edge/Spot cleaning' : 'Limpeza de borda/pontual',
            2: en ? 'Teach route' : 'Rota ensinada',
            3: en ? 'Drawn route' : 'Rota desenhada',
        };
        if (Object.prototype.hasOwnProperty.call(legacyMap, n))
            return legacyMap[n];
        return type != null && type !== '' ? String(type) : '—';
    }

    // Padrão i18n oficial
    const map = {
        0: intl.formatMessage({
            id: 'pudu.area.type.0',
            defaultMessage: 'Zona de limpeza',
        }),
        1: intl.formatMessage({
            id: 'pudu.area.type.1',
            defaultMessage: 'Limpeza de borda/pontual',
        }),
        2: intl.formatMessage({
            id: 'pudu.area.type.2',
            defaultMessage: 'Rota ensinada',
        }),
        3: intl.formatMessage({
            id: 'pudu.area.type.3',
            defaultMessage: 'Rota desenhada',
        }),
    };
    if (Object.prototype.hasOwnProperty.call(map, n)) return map[n];
    return type != null && type !== '' ? String(type) : '—';
}
